import React, { useState } from 'react';
import { hrmsCreateOffer, hrmsSendOffer } from '../../lib/hrmsApi';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { Loader2, FilePlus, Send } from 'lucide-react';

/**
 * Modal to create (and optionally send) an offer letter for a candidate application.
 * Props: application, open, onClose, onCreated
 */
const OfferCreateModal = ({ application, open, onClose, onCreated }) => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    designation: application?.job_title || '',
    department: '',
    ctc: '',
    joining_date: '',
    location: '',
    reporting_manager: '',
    probation_months: 3,
    notice_period_days: 60,
    expires_in_days: 7,
  });

  const submit = async (alsoSend) => {
    if (!form.designation || !form.ctc || !form.joining_date) {
      toast({ title: 'Please fill designation, CTC and joining date' });
      return;
    }
    setSaving(true);
    try {
      const created = await hrmsCreateOffer({
        application_id: application.id,
        ...form,
        probation_months: Number(form.probation_months) || 3,
        notice_period_days: Number(form.notice_period_days) || 60,
        expires_in_days: Number(form.expires_in_days) || 7,
      });
      if (alsoSend) {
        await hrmsSendOffer(created.id);
        toast({ title: 'Offer created & sent', description: `Emailed to ${application?.email}` });
      } else {
        toast({ title: 'Offer saved as draft' });
      }
      onCreated && onCreated(created);
      onClose && onClose();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || '' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogTitle>Create offer · {application?.full_name}</DialogTitle>
        <div className="text-xs text-slate-500 -mt-1 mb-2">{application?.job_title} · {application?.email}</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Designation *</Label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} data-testid="offer-designation" /></div>
          <div><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
          <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
          <div><Label>Annual CTC *</Label><Input value={form.ctc} onChange={(e) => setForm({ ...form, ctc: e.target.value })} placeholder="₹ 24,00,000 / $ 90,000" data-testid="offer-ctc" /></div>
          <div><Label>Joining date *</Label><Input type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} data-testid="offer-joining" /></div>
          <div><Label>Reporting manager</Label><Input value={form.reporting_manager} onChange={(e) => setForm({ ...form, reporting_manager: e.target.value })} /></div>
          <div><Label>Probation (months)</Label><Input type="number" value={form.probation_months} onChange={(e) => setForm({ ...form, probation_months: e.target.value })} /></div>
          <div><Label>Notice (days)</Label><Input type="number" value={form.notice_period_days} onChange={(e) => setForm({ ...form, notice_period_days: e.target.value })} /></div>
          <div><Label>Offer valid for (days)</Label><Input type="number" value={form.expires_in_days} onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })} /></div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="outline" onClick={() => submit(false)} disabled={saving} data-testid="offer-save-draft">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FilePlus className="h-4 w-4 mr-1" /> Save draft</>}
          </Button>
          <Button onClick={() => submit(true)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="offer-send-now">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Save &amp; send now</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OfferCreateModal;
